import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { SpcSteps } from './SpcModels';
import { useParams } from 'react-router-dom';
import SpcViewModel from './SpcViewModel';
import { Appl } from '../../../../core/Appl';
import { Button, Icon } from '../../../../core/components/Index';
import Helper, { formatDate } from '../../../../core/services/Helper';
import { useState } from 'react';
import React from 'react';
import SavedEstimates from './SavedEstimates';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../core/components/OptionSelectionAccordian';

const Estimate = observer(() => {
  const vm = Appl.Services.get<SpcViewModel>('SpcViewModel');
  let { memberId } = useParams();
  const [openIndexes, setOpenIndexes] = useState<{ [key: number]: boolean }>({});

  useAsyncEffect(async () => {
    await vm.loadStepAsync(SpcSteps.Estimate, memberId!);
    if (vm.model.estimateDetails) {
      const initialState: { [key: number]: boolean } = {};
      vm.model.estimateDetails.forEach((_, index) => {
        initialState[index] = true;
      });
      setOpenIndexes(initialState);
    }
  }, []);

  const searchParams = new URLSearchParams();

  //Getting Variables from URL:
    useAsyncEffect(async () => {
        const paramsToSearch = new URLSearchParams(window.location.search);
        const url_spcTypeId = paramsToSearch.get('spcTypeId');
        const url_spcTypeSubId = paramsToSearch.get('spcTypeSubId');
        searchParams.set('spcTypeId', String(url_spcTypeId));
        searchParams.set('spcTypeSubId', String(url_spcTypeSubId));
    });

  function formatFinancialYear(fy: string) 
  {
    const parts = fy.split('-');
    if (parts.length === 2) 
    {
      let firstYear = parseInt(parts[0].replace('FY',""));
      let secondYear = firstYear+1;
      return `FY ${firstYear}/${secondYear}`;
    }
  }

  function LeavePeriodSection() 
  {
    //Rendering no accordion if only one breakdown exists: 
    const breakdowns = vm.model.finalEstimate?.estimateBreakdowns ?? [];
    const isSingle = breakdowns.length === 1;

    if (isSingle) 
    {
      const estBreakdown = breakdowns[0];

      return (
        <div className="w-full max-w-[849px]">

          <div className="pt-[20px] flex flex-col md:flex-row gap-[12px] mb-[0px]">

                      {/* ---------- LEFT SIDE (What You Entered) ---------- */}
                      <div className="w-full md:w-[49%] pl-[24px]">

                        <p className="text-[15px] font-semibold mb-[24px]">
                          What You Entered
                        </p>

                        {/* Type 1 */}
                        {estBreakdown.spcTypeId === 1 && (
                          <ul className="list-disc pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Employer:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.employer}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Membership Tier:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.membershipTier}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Medical Leave Period:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                       {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                        ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                        : ""}
                                    </span>
                                </div>
                              </li>

                               <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Estimated Hours of Leave:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                       {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                    Compensation Used:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                    </span>
                                </div>
                              </li>
                          </ul>
                        )}

                        {/* Type 2 */}
                          {estBreakdown.spcTypeId === 2 && (
                            <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                              <li className="flex items-start gap-2">
                                {/* Bullet */}
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                {/* Label + value */}
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  {/* Label: never wrap */}
                                  <span className="whitespace-nowrap">
                                    Employer:
                                  </span>

                                  {/* Value: wraps and hangs left */}
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.employer}
                                  </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                   Membership Entry Date:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {formatDate(estBreakdown.mbrEntryDate)}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                   Membership Tier:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.membershipTier}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                   Date of Employment:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                    ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                    : ""}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Estimated Hours of Leave:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                    </span>
                                </div>
                              </li>

                              <li className="flex items-start gap-2">

                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />

                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Compensation Used:
                                   </span>

                                    <span className="font-bold tracking--1_5percent break-words">
                                      {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                    </span>
                                </div>
                              </li>
                            </ul>
                          )}

                        {/* Type 3 */}
                        {estBreakdown.spcTypeId === 3 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Withdrawal Date:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {formatDate(estBreakdown.withdrawDate)}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Amount Withdrawn:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {Helper.formatCurrency(estBreakdown.withdrawAmount)}
                                    </span>
                                </div>
                            </li>
                          </ul>
                        )}

                        {/* Type 4 */}
                        {estBreakdown.spcTypeId === 4 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Membership Entry Date:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {formatDate(estBreakdown.mbrEntryDate)}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Membership Tier at Entry:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                       {estBreakdown.membershipTier}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Employer:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                       {estBreakdown.employer}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Date of Service:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                       {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                        ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                        : ""}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Est. Hours:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                       {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Est. Compensation:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                    </span>
                                </div>
                            </li>
                          </ul>
                        )}
                      </div>

                      {/* ---------- RIGHT SIDE (System Applied) ---------- */}
                      <div className="w-full md:w-[49%] pl-[24px]">

                        <p className="text-[15px] font-semibold mb-[24px]">
                          What the System Applied<sup>†</sup>
                        </p>

                        <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                        {estBreakdown.leaveSegments?.map(function (leaveSegment, ind) {
                          return (
                            <React.Fragment key={ind}>

                              {estBreakdown.spcTypeId !== 3 && (
                                <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Contribution Rate ({formatFinancialYear(leaveSegment?.fiscalYear!)}):
                                      </span>
                                        <span className="font-bold tracking--1_5percent break-words">
                                          {leaveSegment?.contributionRate != null
                                            ? (leaveSegment.contributionRate * 100).toFixed(2) + "%"
                                            : ""}
                                        </span>
                                    </div>
                                </li>
                              )}

                            </React.Fragment>
                          );
                        })}

                        {estBreakdown.leaveSegments &&
                          estBreakdown.leaveSegments.length > 0 && (
                             <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Interest Factor:
                                      </span>
                                      <span className="tracking--1_5percent">
                                        <span className='font-bold'>
                                          {estBreakdown.leaveSegments[0].compoundInterestFactor?.toFixed(5)}
                                        </span>
                                        <span className='!font-normal'>
                                            {" "}({formatDate(estBreakdown.leaveSegments[0]?.startDate)} to present)
                                        </span>
                                      </span>
                                    </div>
                                </li>
                            
                          )}

                          {estBreakdown.spcTypeId !== 3 && (
                              <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Hourly Compensation:
                                      </span>
                                      <span className="tracking--1_5percent">
                                        <span className='font-bold'>
                                          {Helper.formatCurrency(estBreakdown.hourlyPay)}
                                        </span>
                                      </span>
                                    </div>
                                </li>
                            )}

                      </ul>

                      </div>

                    </div>
                  <div className="w-full max-w-[849px] border-t-[1px] border-[#D6D6D6] mt-[20px]"></div>
                </div>
              );
    }

    else {
      return (
        <Accordion type="multiple" className="w-full max-w-[849px]">

          {vm.model.finalEstimate?.estimateBreakdowns?.map(function(estBreakdown, index) 
          {
            let titleText: React.ReactNode = `Leave Period ${index + 1} - Estimate: ${Helper.formatCurrency(estBreakdown.estimate ?? 0)}`;

            if (estBreakdown.spcTypeId === 3) 
            {
              titleText = (
                      <>
                        {/* Mobile */}
                        <span className="hidden md:block">
                          Withdrawal Contributions {index + 1} - Estimate:{" "}
                          {Helper.formatCurrency(estBreakdown.estimate ?? 0)}
                        </span>

                        {/* md+ */}
                        <div className="md:hidden">
                          <div>Withdrawal Contributions {index + 1}</div>
                          <div>
                            - Estimate: {Helper.formatCurrency(estBreakdown.estimate ?? 0)}
                          </div>
                        </div>
                      </>
                      );
            } 

            return (
              <AccordionItem 
                key={`period-${index}`} 
                value={`period-${index}`} 
                withBorder={false}
              >
              
                  {/* ---------- Trigger ---------- */}
                  <AccordionTrigger className="px-0">
                    <div className="flex w-full items-start text-left">
                      <div className="text-[15px] font-bold">
                        {titleText}
                      </div>
                    </div>
                  </AccordionTrigger>

                  <div className="w-full border-b-[1px] border-[#D6D6D6] mt-[0px]"></div>

                 <AccordionContent>
                    <div className="pt-[20px] flex flex-wrap gap-[12px] mb-[0px]">

                      {/* ---------- LEFT SIDE (What You Entered) ---------- */}
                      <div className="w-full md:w-[49%] pl-[24px]">

                        <p className="text-[15px] font-semibold mb-[24px]">
                          What You Entered
                        </p>

                        {/* ---------- TYPE 1 ---------- */}
                        {estBreakdown.spcTypeId === 1 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Employer:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.employer}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Membership Tier:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.membershipTier}
                                    </span>
                                </div>
                            </li>

                            <li className="flex items-start gap-2">
                                <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                <div className="flex flex-col md:flex-row md:gap-1">
                                  <span className="whitespace-nowrap">
                                      Medical Leave Period:
                                   </span>
                                    <span className="font-bold tracking--1_5percent break-words">
                                      {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                      ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                      : ""}
                                    </span>
                                </div>
                            </li>
                            
                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Estimated Hours of Leave:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Compensation Used:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                  {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                  </span>
                              </div>
                            </li>
                          </ul>
                        )}

                        {/* ---------- TYPE 2 ---------- */}
                        {estBreakdown.spcTypeId === 2 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Employer:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.employer}
                                  </span>
                              </div>
                            </li>

                             <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Membership Entry Date:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {formatDate(estBreakdown.mbrEntryDate)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Membership Tier:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.membershipTier}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Date of Employment:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                      ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                      : ""}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                   Estimated Hours of Leave:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                   Compensation Used:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                  </span>
                              </div>
                            </li>

                          </ul>
                        )}

                        {/* ---------- TYPE 3 ---------- */}
                        {estBreakdown.spcTypeId === 3 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Withdrawal Date:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                  {formatDate(estBreakdown.withdrawDate)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Amount Withdrawn:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatCurrency(estBreakdown.withdrawAmount)}
                                  </span>
                              </div>
                            </li>
                          </ul>
                        )}

                        {/* ---------- TYPE 4 ---------- */}
                        {estBreakdown.spcTypeId === 4 && (
                          <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Membership Entry Date:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {formatDate(estBreakdown.mbrEntryDate)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Membership Tier at Entry:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.membershipTier}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  SCERS Employer:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.employer}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Date of Service:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {estBreakdown.leavePeriodFrom && estBreakdown.leavePeriodTo
                                    ? `${formatDate(estBreakdown.leavePeriodFrom)} – ${formatDate(estBreakdown.leavePeriodTo)}`
                                    : ""}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                  Est. Hours:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatWholeNumber(estBreakdown.estimatedHoursOfLeave)}
                                  </span>
                              </div>
                            </li>

                            <li className="flex items-start gap-2">
                              <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                              <div className="flex flex-col md:flex-row md:gap-1">
                                <span className="whitespace-nowrap">
                                    Est. Compensation:
                                  </span>
                                  <span className="font-bold tracking--1_5percent break-words">
                                    {Helper.formatCurrency(estBreakdown.compensationUsed)} {estBreakdown.compensationFrequency}
                                  </span>
                              </div>
                            </li>
                          </ul>
                        )}

                      </div>

                      {/* ---------- RIGHT SIDE (System Applied) ---------- */}
                      <div className="w-full md:w-[49%] pl-[24px]">

                        <p className="text-[15px] font-semibold mb-[24px]">
                          What the System Applied<sup>†</sup>
                        </p>

                        <ul className="pl-[10px] space-y-[0px] text-[15px] leading-[175%]">

                          {estBreakdown.leaveSegments?.map(function (leaveSegment, ind) {
                            return (
                              <React.Fragment key={ind}>
                                {estBreakdown.spcTypeId !== 3 && (
                                  <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Contribution Rate ({formatFinancialYear(leaveSegment?.fiscalYear!)}):
                                        </span>
                                        <span className="font-bold tracking--1_5percent break-words">
                                          {leaveSegment?.contributionRate != null
                                        ? (leaveSegment.contributionRate * 100).toFixed(2) + "%"
                                        : ""}
                                        </span>
                                    </div>
                                  </li>
                                )}
                              </React.Fragment>
                            );
                          })}

                            {estBreakdown.leaveSegments &&
                            estBreakdown.leaveSegments.length > 0 && (
                              <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Interest Factor:
                                      </span>
                                      <span className="tracking--1_5percent">
                                        <span className='font-bold'>
                                          {estBreakdown.leaveSegments[0].compoundInterestFactor?.toFixed(5)}
                                        </span>
                                        <span className='!font-normal'>
                                            {" "}({formatDate(estBreakdown.leaveSegments[0]?.startDate)} to present)
                                        </span>
                                      </span>
                                    </div>
                                </li>
                            )}

                            {estBreakdown.spcTypeId !== 3 && (
                              <li className="flex items-start gap-2">
                                    <span className="mt-[10px] block h-1 w-1 rounded-full bg-black flex-shrink-0" />
                                    <div className="flex flex-col md:flex-row md:gap-1">
                                      <span className="whitespace-nowrap">
                                          Hourly Compensation:
                                      </span>
                                      <span className="tracking--1_5percent">
                                        <span className='font-bold'>
                                          {Helper.formatCurrency(estBreakdown.hourlyPay)}
                                        </span>
                                      </span>
                                    </div>
                                </li>
                            )}

                        </ul>
                      </div>
                      
                    </div>

                  </AccordionContent>


              </AccordionItem>
            );
          })}
          <div className="w-full max-w-[849px] border-t-[1px] border-[#D6D6D6] mt-[-1px]"></div>
        </Accordion>
      );
    }
}

  return (
    <>
      <div className="max-w-full mx-auto w-full max-w-[880px] p-[10px]">
        <div>
          <h1 className="text-[32px] tracking-[-1%] text-[#363636] !font-semibold mb-4">{vm.spcLovModel.serviceTypeListItems?.find(o=>o.id===vm.model?.spcTypeId)?.name} — Service Purchase Estimate</h1>
          <p className="tracking--1percent"></p>
        </div>
        <div className="flex flex-col items-center mt-[20px]">
          <div className="shadow-xl mt-[24px] w-full max-w-[604px] min-h-[127px] bg-[#F7F3ED] border border-[#CF7119] border-l-[10px] rounded-[10px] px-[24px] py-[20px] flex flex-col items-center justify-center gap-[8px]">
            <p className="text-center text-[18px] font-bold leading-[120%] m-0">
              Your Preliminary Service Purchase Estimate is
            </p>

            <p className="text-center text-[clamp(30px,6vw,40px)] font-bold leading-[100%] m-0">
              {Helper.formatCurrency(vm.model?.finalEstimate?.purchaseEstimate ?? 0)}
              <span className="text-[#CF7119] text-[clamp(30px,6vw,40px)]"><sup>*</sup></span>
            </p>
          </div>

          <p className="w-full max-w-[604px] mt-[12px] text-[15px] text-center leading-[150%] m-0">
            *Note: This is an online estimate and may differ from the official cost.
          </p>
        </div>
        <div>
          <h2 className="mt-[32px] text-[22px] font-semibold tracking-[0px]">
            Estimate Breakdown
          </h2>
          <p className="mt-[10px] text-[15px] leading-[175%]">
            This estimate is based on the information you provided and the values automatically determined by SCERS.
          </p>
          <div>
            <LeavePeriodSection />
          </div>
        </div>
        <div>
          <p className="mt-[10px] mb-[40px] text-[14px] italic leading-[150%]">
            <sup>†</sup> These values are system-generated for estimate purposes only. Official calculations will use the verified contribution rates and interest factors in effect for your account.
          </p>
          <div className="flex mt-6 flex-wrap gap-[24px] sm:flex-nowrap">
            <Button 
              className='w-[146px] h-[39px]'
              title="Print Estimate" 
              rounded
              onClick={async () => {
                await vm.onPrintEstimateAsync2();
              }}
            />
            <Button 
              title="Edit Estimate" 
              className='w-[146px] h-[39px]' 
              severity='warning' 
              outlined rounded 
              onClick={async () => await vm.navigateToStepAsync(`${SpcSteps.Summary}?${searchParams.toString()}`)} 
            />
            <Button 
              title="New Estimate" 
              className='w-[146px] h-[39px]' 
              severity='warning' 
              outlined rounded 
              onClick={async () => {
                  await vm.beginNewEstimate(memberId!,true);  
                }}
            />
          </div>
          <h2 className="mt-[40px] text-[24px] font-semibold">What You Can Do Next</h2>
            <p className="mt-[12px] text-[15px] leading-[170%]">
              Your preliminary estimate <span className="font-bold">has been saved automatically.</span> 
            </p>
            <p className="mt-[20px] mb-[20px] text-[15px] leading-[170%]">At this point, you can:</p>
          <ul className="list-disc pl-[24px] mt-[8px] space-y-[4px] text-[15px] leading-[170%]">
            <li><span className="font-bold">Print this estimate</span> for your records.</li>
            <li><span className="font-bold">Edit this estimate</span> to update leave periods or compensation.</li>
            <li><span className="font-bold">Generate a new estimate</span> for a different service credit type or leave period.</li>
            <li><span className="font-bold">Review a saved</span> estimate using the list below.</li>
          </ul>
        </div>
        <div>
          <h2 className="mt-[40px] mb-[20px] text-[24px] font-semibold">Your Saved Estimates</h2>
           <div className="max-w-[80vw] overflow-auto">
            <SavedEstimates />
           </div>
        </div>
        <div className="flex mt-[32px] w-[159px] h-[39px] flex-wrap gap-2 sm:flex-nowrap">
          <Button 
            title="Return to Home" 
            rounded
            onClick={() => window.location.href = Appl.Setting.MyScersPortalUrl}
          />
        </div>
      </div>
    </>
  );
});

export default Estimate;
